/*
  Bundle draft-order checkout for PureAmor.

  When the cart contains lines tagged with the hidden `_pa_bundle_key`
  property (added by sections/pureamor-build-your-own-bundle.liquid), the
  checkout click is intercepted and the whole cart is sent to the Supabase
  `bundle-draft-checkout` edge function, which validates the bundle, creates
  a Shopify Draft Order with line-level percentage discounts and returns the
  draft order invoice URL. The customer pays on Shopify's standard checkout
  page (Apple Pay / wallets included), so no Shopify Plus is required.

  Checkout clicks are always intercepted and routed from the live cart
  state: carts without bundle lines continue straight to the native Shopify
  checkout (the original cart form is re-submitted, keeping notes and
  attributes), so there is no activation race. If the backend cannot be
  reached while a bundle IS in the cart, checkout shows a retryable error
  instead of silently falling back to the full-price native checkout.

  Also responsible for:
  - Grouping bundle component lines into one visual summary row in the cart
    drawer (quick-cart) and the cart page, with estimated savings.
  - Replacing the visible subtotal with the bundle-adjusted estimate.
  - Hiding accelerated checkout buttons (Apple Pay / wallet buttons rendered
    by content_for_additional_checkout_buttons) while a bundle is in the
    cart — those buttons bypass the draft-order flow and would charge full
    price. They come back as soon as the bundle is removed.
  - Keeping the free-gift line (hidden `_free_gift` property, normally made
    free by the Shopify Function discount) free inside the draft order by
    tagging it as a 100%-discounted bundle gift in the checkout payload.

  Backend rules live in supabase/functions/bundle-draft-checkout/index.ts
  (project: pureamor-bundle-checkout). Theme tiers and backend tiers must be
  kept in sync — the backend is the source of truth for the final discount.
*/

(function () {
  var ENDPOINT = window.PUREAMOR_BUNDLE_DRAFT_CHECKOUT_URL || 'https://cvgujbudnaovsebfirgw.supabase.co/functions/v1/bundle-draft-checkout';
  var submitting = false;
  var suppressCartObserver = false;
  var refreshTimers = {};

  var BUNDLE_RULES = {
    'build-your-own-bundle': { title: 'Build Your Own Bundle', percent: 42 }
  };

  function money(cents, currency) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'USD'
      }).format((Number(cents) || 0) / 100);
    } catch (_error) {
      return '$' + (((Number(cents) || 0) / 100).toFixed(2));
    }
  }

  function escapeHtml(value) {
    return String(value || '').replace(/[&<>"']/g, function (character) {
      return {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
      }[character];
    });
  }

  function normalizeProperties(properties) {
    if (!properties || typeof properties !== 'object') return {};
    return Object.keys(properties).reduce(function (result, key) {
      var value = properties[key];
      if (value !== null && typeof value !== 'undefined' && String(value).trim()) {
        result[key] = String(value);
      }
      return result;
    }, {});
  }

  function hasBundleLines(cart) {
    return Boolean(cart && Array.isArray(cart.items) && cart.items.some(function (item) {
      return item.properties && item.properties._pa_bundle_key;
    }));
  }

  function bundleGroupId(item) {
    var properties = normalizeProperties(item.properties);
    var bundleKey = properties._pa_bundle_key;
    if (!bundleKey) return '';
    return bundleKey + ':' + (properties._pa_bundle_instance || 'default');
  }

  /* This figure is a QUOTE — the backend applies the real discount, per line,
     from the percentage alone. So it has to round the way Shopify does or the
     shopper is shown one price and charged another.

     Shopify TRUNCATES a line-level percentage discount to the cent. Every
     price here ends in .99, and 10/15/20/25/30% of a .99 price lands on a
     half-cent (15% of $35.99 = $5.3985), so Math.round went up where Shopify
     went down — a cent per line, always in the direction that quotes LESS
     than checkout collects. Measured on live: this said $75.61 against a
     checkout that charged $75.65. Math.floor matches it exactly.

     (Safe against float error: linePrice and percent are integers, so the
     product is exact, and IEEE division returns an exact quotient whenever
     the true result is a whole number of cents.) */
  function bundleLineDiscount(item) {
    var properties = normalizeProperties(item.properties);
    var rule = BUNDLE_RULES[properties._pa_bundle_key];
    var linePrice = Number(item.final_line_price || item.line_price || 0);
    var percent = properties._pa_bundle_role === 'free-gift'
      ? 100
      : Number(properties._pa_bundle_discount_percent || (rule && rule.percent) || 0);
    return Math.floor(linePrice * percent / 100);
  }

  function bundleGroups(cart) {
    var groups = {};
    (cart.items || []).forEach(function (item) {
      var groupId = bundleGroupId(item);
      if (!groupId) return;
      var properties = normalizeProperties(item.properties);
      var rule = BUNDLE_RULES[properties._pa_bundle_key] || {};
      if (!groups[groupId]) {
        groups[groupId] = {
          id: groupId,
          key: properties._pa_bundle_key,
          title: properties._pa_bundle_title || rule.title || 'PureAmor bundle',
          percent: Number(properties._pa_bundle_discount_percent || rule.percent || 0),
          instance: properties._pa_bundle_instance || 'default',
          image: '',
          items: [],
          quantity: 0,
          subtotal: 0,
          discount: 0
        };
      }
      if (!groups[groupId].image && item.image) groups[groupId].image = item.image;
      groups[groupId].items.push(item);
      groups[groupId].quantity += Number(item.quantity || 0);
      groups[groupId].subtotal += Number(item.final_line_price || item.line_price || 0);
      groups[groupId].discount += bundleLineDiscount(item);
    });
    return Object.keys(groups).map(function (key) { return groups[key]; });
  }

  function safeSelector(value) {
    if (window.CSS && CSS.escape) return CSS.escape(value);
    return String(value).replace(/"/g, '\\"');
  }

  function ensureBundleCartStyles() {
    if (document.querySelector('[data-pa-bundle-cart-style]')) return;
    var style = document.createElement('style');
    style.setAttribute('data-pa-bundle-cart-style', '');
    style.textContent = [
      '.pa-bundle-cart-summary{display:grid;grid-template-columns:72px minmax(0,1fr);gap:12px;padding:14px 0;border-bottom:1px solid var(--color-border,rgba(0,0,0,.1));}',
      '.quick-cart__items .pa-bundle-cart-summary{grid-template-columns:64px minmax(0,1fr);}',
      '.pa-bundle-cart-summary__media{border-radius:8px;background:var(--color-background-footer,#ebf0f2);min-height:72px;display:grid;place-items:center;color:var(--color-text,#102b26);font-size:11px;letter-spacing:.08em;text-transform:uppercase;overflow:hidden;}',
      '.pa-bundle-cart-summary__media img{width:100%;height:100%;object-fit:cover;display:block;}',
      '.pa-bundle-cart-summary__body{display:grid;gap:6px;align-content:start;}',
      '.pa-bundle-cart-summary__title{margin:0;font-weight:700;line-height:1.25;}',
      /* Sizes/opacity now come from the drawer's own fs-* / t-opacity-* classes
         so the block matches the surrounding line items. */
      '.pa-bundle-cart-summary__meta{margin:0;line-height:1.35;}',
      '.pa-bundle-cart-summary__items{margin:2px 0 0;padding:0;list-style:none;line-height:1.45;}',
      '.pa-bundle-cart-summary__items li{margin:0;}',
      '.pa-bundle-cart-summary__prices{display:flex;gap:8px;align-items:baseline;flex-wrap:wrap;font-size:13px;}',
      '.pa-bundle-cart-summary__compare{text-decoration:line-through;opacity:.55;}',
      '.pa-bundle-cart-summary__price{font-weight:700;}',
      '.pa-bundle-cart-summary__save{color:var(--color-products-sale-price,#d21404);font-size:12px;}',
      '.pa-bundle-cart-summary__remove{justify-self:start;border:0;background:transparent;padding:0;color:inherit;opacity:.7;text-decoration:underline;cursor:pointer;font:inherit;font-size:12px;}',
      '.pa-bundle-cart-summary__remove:hover{opacity:1;}',
      '[data-pa-hidden-bundle-line="true"]{display:none!important;}',
      /* Wallet / accelerated checkout buttons skip the draft-order flow, so
         hide them while a bundle is in the cart. */
      'body[data-pa-bundle-checkout="draft"] .additional-checkout-buttons{display:none!important;}'
    ].join('\n');
    document.head.appendChild(style);
  }

  /*
    Sprint 3 feedback: the drawer should list what is actually IN the bundle.
    Each BYOB line carries `Selection` (the size, e.g. "6ml Roll-On") and
    `Scent` properties; fall back to the product title for anything added by a
    different bundle flow. Same typography classes the drawer's own line items
    use, so it reads as part of the drawer rather than a bolt-on.
  */
  function bundleItemsHtml(group) {
    var rows = (group.items || []).map(function (item) {
      var props = normalizeProperties(item.properties);
      var label = [props.Selection, props.Scent].filter(Boolean).join(' · ') ||
        item.product_title || item.title || '';
      if (!label) return '';
      var qty = Number(item.quantity || 0);
      return '<li>' + (qty > 1 ? qty + ' × ' : '') + escapeHtml(label) + '</li>';
    }).filter(Boolean);
    if (!rows.length) return '';
    return '<ul class="pa-bundle-cart-summary__items fs-body-60 t-opacity-60">' + rows.join('') + '</ul>';
  }

  function summaryHtml(group, currency) {
    var total = Math.max(0, group.subtotal - group.discount);
    return '' +
      '<div class="pa-bundle-cart-summary" data-pa-bundle-cart-summary="' + escapeHtml(group.id) + '">' +
        '<div class="pa-bundle-cart-summary__media" aria-hidden="true">' +
          (group.image ? '<img src="' + escapeHtml(group.image) + '" alt="">' : 'Bundle') +
        '</div>' +
        '<div class="pa-bundle-cart-summary__body">' +
          '<p class="pa-bundle-cart-summary__title fs-product-card-title">' + escapeHtml(group.title) + '</p>' +
          '<p class="pa-bundle-cart-summary__meta fs-body-60 t-opacity-60">' + group.quantity + ' bundle item' + (group.quantity === 1 ? '' : 's') + (group.percent ? ' · ' + group.percent + '% off' : '') + '</p>' +
          bundleItemsHtml(group) +
          '<div class="pa-bundle-cart-summary__prices fs-body-75">' +
            '<span class="pa-bundle-cart-summary__compare">' + money(group.subtotal, currency) + '</span>' +
            '<span class="pa-bundle-cart-summary__price">' + money(total, currency) + '</span>' +
            '<span class="pa-bundle-cart-summary__save">Save ' + money(group.discount, currency) + '</span>' +
          '</div>' +
          '<button type="button" class="pa-bundle-cart-summary__remove" data-pa-remove-bundle="' + escapeHtml(group.id) + '">Remove bundle</button>' +
        '</div>' +
      '</div>';
  }

  function renderBundleCartSummaries(cart) {
    ensureBundleCartStyles();
    suppressCartObserver = true;
    document.querySelectorAll('[data-pa-bundle-cart-summary]').forEach(function (summary) { summary.remove(); });
    document.querySelectorAll('[data-pa-bundle-cart-total-preview]').forEach(function (summary) { summary.remove(); });
    document.querySelectorAll('[data-pa-hidden-bundle-line="true"]').forEach(function (line) {
      line.removeAttribute('data-pa-hidden-bundle-line');
    });

    var groups = bundleGroups(cart);
    groups.forEach(function (group) {
      var firstLinesByParent = [];
      group.items.forEach(function (item) {
        var lines = document.querySelectorAll('[data-input-item][data-key="' + safeSelector(item.key) + '"]');
        lines.forEach(function (line) {
          if (!line) return;
          var parent = line.parentNode;
          if (parent && !firstLinesByParent.some(function (entry) { return entry.parent === parent; })) {
            firstLinesByParent.push({ parent: parent, line: line });
          }
          line.setAttribute('data-pa-hidden-bundle-line', 'true');
        });
      });
      firstLinesByParent.forEach(function (entry) {
        entry.line.insertAdjacentHTML('beforebegin', summaryHtml(group, cart.currency));
      });
    });

    renderBundleTotalPreview(cart, groups);
    window.setTimeout(function () { suppressCartObserver = false; }, 120);
  }

  function subtotalTargets() {
    return Array.from(document.querySelectorAll('.quick-cart__footer-subtotal > span, .cart__footer-subtotal > span'));
  }

  function restoreSubtotalDisplays() {
    subtotalTargets().forEach(function (target) {
      var original = target.getAttribute('data-pa-original-subtotal-text');
      if (original) target.textContent = original;
      target.removeAttribute('data-pa-original-subtotal-text');
      target.removeAttribute('data-pa-bundle-adjusted-subtotal');
    });
  }

  function updateSubtotalDisplays(cart, discount) {
    if (!discount) {
      restoreSubtotalDisplays();
      return;
    }
    var estimatedTotal = Math.max(0, Number(cart.total_price || 0) - discount);
    subtotalTargets().forEach(function (target) {
      if (!target.getAttribute('data-pa-original-subtotal-text')) {
        target.setAttribute('data-pa-original-subtotal-text', target.textContent.trim());
      }
      target.setAttribute('data-pa-bundle-adjusted-subtotal', 'true');
      target.textContent = money(estimatedTotal, cart.currency);
    });
  }

  function renderBundleTotalPreview(cart, groups) {
    var discount = groups.reduce(function (total, group) { return total + group.discount; }, 0);
    updateSubtotalDisplays(cart, discount);
    if (!discount) return;
    var html = '' +
      '<div data-pa-bundle-cart-total-preview style="display:grid;gap:5px;margin:10px 0 4px;padding:10px 0;border-top:1px solid var(--color-border,rgba(0,0,0,.1));border-bottom:1px solid var(--color-border,rgba(0,0,0,.1));">' +
        '<div style="display:flex;justify-content:space-between;gap:12px;font-size:13px;line-height:1.35;">' +
          '<span>Bundle savings at checkout</span>' +
          '<strong style="color:var(--color-products-sale-price,#d21404);">-' + money(discount, cart.currency) + '</strong>' +
        '</div>' +
      '</div>';
    var targets = document.querySelectorAll('.quick-cart__footer-subtotal, .cart__footer-subtotal');
    targets.forEach(function (target) {
      target.insertAdjacentHTML('beforebegin', html);
    });
  }

  async function removeBundle(groupId, button) {
    if (button) {
      button.disabled = true;
      button.textContent = 'Removing...';
    }
    var cart = await fetchCart();
    var group = bundleGroups(cart).filter(function (item) { return item.id === groupId; })[0];
    if (!group) return;
    var updates = {};
    group.items.forEach(function (item) {
      updates[item.key] = 0;
    });
    await fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: updates })
    });

    /* Removing the bundle can drop the cart under the free-gift threshold, and
       free-gift.js reconciles ASYNCHRONOUSLY, off the cart:updated event.
       Reloading the moment the lines were zeroed rendered the page while the
       now-unqualified gift was still in the cart, so the shopper was left
       looking at a header badge reading 1 and a phantom gift line in the
       drawer, for a cart the API already reported as empty. The gift removal
       landed just after that render and nothing re-renders, so it never
       cleared -- only the next navigation fixed it.

       So: tell free-gift.js the cart changed, wait for the count to stop
       moving, and reload a settled cart. Bounded, and it falls through to the
       reload regardless -- a slow reconcile should delay this, never block it. */
    await settleCart();
    window.location.reload();
  }

  /* Poll until the cart's item count holds steady across two reads, so callers
     can act on a cart that other listeners have finished mutating. */
  async function settleCart() {
    var last = null;
    try {
      var cart = await fetchCart();
      last = cart.item_count;
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
    } catch (err) { return; }
    for (var i = 0, stable = 0; i < 12 && stable < 2; i++) {
      await new Promise(function (resolve) { setTimeout(resolve, 200); });
      try {
        var fresh = await fetchCart();
        stable = fresh.item_count === last ? stable + 1 : 0;
        last = fresh.item_count;
      } catch (err) { return; }
    }
  }

  function checkoutButtons() {
    return Array.from(document.querySelectorAll('button[name="checkout"], input[name="checkout"], a[href="/checkout"], a[href$="/checkout"]'));
  }

  function setButtonsBusy(isBusy) {
    checkoutButtons().forEach(function (button) {
      if (isBusy) {
        button.setAttribute('aria-busy', 'true');
        if (button.tagName === 'BUTTON' || button.tagName === 'INPUT') {
          button.setAttribute('data-pa-original-disabled', button.disabled ? 'true' : 'false');
          button.disabled = true;
        }
      } else {
        button.removeAttribute('aria-busy');
        if ((button.tagName === 'BUTTON' || button.tagName === 'INPUT') && button.getAttribute('data-pa-original-disabled') !== 'true') {
          button.disabled = false;
        }
        button.removeAttribute('data-pa-original-disabled');
      }
    });
  }

  function showError(message) {
    var note = document.querySelector('[data-pa-bundle-checkout-error]');
    if (!note) {
      note = document.createElement('div');
      note.setAttribute('data-pa-bundle-checkout-error', '');
      note.style.marginTop = '12px';
      note.style.color = '#a32323';
      note.style.fontSize = '13px';
      note.style.lineHeight = '1.35';
      var target = document.querySelector('.cart__input-buttons, .quick-cart__submit') || document.querySelector('form[action="/cart"]');
      if (target && target.parentNode) target.parentNode.insertBefore(note, target.nextSibling);
    }
    note.textContent = message || 'Unable to prepare bundle checkout. Please try again.';
  }

  function clearError() {
    document.querySelectorAll('[data-pa-bundle-checkout-error]').forEach(function (note) {
      note.textContent = '';
    });
  }

  function buildPayload(cart) {
    // The theme free gift (hidden `_free_gift` property) is normally made
    // free by the Shopify Function discount, but automatic discounts do not
    // apply to draft orders. Tag it as a bundle gift so the backend gives it
    // a 100% line discount instead. Gift-role lines are excluded from bundle
    // size validation on the backend.
    var firstGroup = bundleGroups(cart)[0] || null;

    return {
      cart: {
        token: cart.token || '',
        currency: cart.currency || '',
        items: (cart.items || []).map(function (item) {
          var properties = normalizeProperties(item.properties);
          if (firstGroup && properties._free_gift && !properties._pa_bundle_key) {
            properties._pa_bundle_key = firstGroup.key;
            properties._pa_bundle_instance = firstGroup.instance;
            properties._pa_bundle_role = 'free-gift';
          }
          return {
            id: item.id,
            variant_id: item.variant_id || item.id,
            quantity: item.quantity,
            title: item.title,
            properties: properties
          };
        })
      }
    };
  }

  async function fetchCart() {
    var response = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!response.ok) throw new Error('Unable to read cart.');
    return response.json();
  }

  async function createDraftCheckout(cart) {
    var response = await fetch(ENDPOINT, {
      method: 'POST',
      credentials: 'omit',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(buildPayload(cart))
    });
    var payload = await response.json().catch(function () { return {}; });
    if (!response.ok || payload.ok === false) {
      throw new Error(payload.error || 'Unable to prepare bundle checkout.');
    }
    return payload;
  }

  function failCheckout(message) {
    showError(message);
    submitting = false;
    setButtonsBusy(false);
  }

  function nativeCheckout(trigger) {
    var form = trigger && (trigger.form || (trigger.closest ? trigger.closest('form') : null));
    if (form && /\/cart\/?$/.test(form.getAttribute('action') || '')) {
      // Preserve the native flow (cart note / attributes post together with
      // the form). form.submit() skips submit listeners, so this cannot
      // re-enter the interceptor; the hidden field stands in for the
      // submitter's name="checkout".
      if (!form.querySelector('input[type="hidden"][name="checkout"]')) {
        var checkoutField = document.createElement('input');
        checkoutField.type = 'hidden';
        checkoutField.name = 'checkout';
        checkoutField.value = '';
        form.appendChild(checkoutField);
      }
      form.submit();
      return;
    }
    window.location.href = '/checkout';
  }

  async function prepareCheckout(event, trigger) {
    if (submitting) return;
    if (!trigger) return;
    submitting = true;

    var cart;
    try {
      cart = await fetchCart();
    } catch (_error) {
      // Cart unreadable. If the last known state had a bundle, refuse to
      // proceed (native checkout would silently charge the undiscounted
      // price); otherwise continue the native flow.
      if (lastCartHadBundle) {
        failCheckout('Unable to prepare bundle checkout. Please try again.');
      } else {
        nativeCheckout(trigger);
      }
      return;
    }

    syncBundleState(cart);

    // No bundle in the cart — continue the native Shopify checkout.
    if (!hasBundleLines(cart)) {
      nativeCheckout(trigger);
      return;
    }

    clearError();
    setButtonsBusy(true);

    // The backend rejects carts over its hard limits (50 units per line,
    // 200 lines) with a generic error — pre-check so the customer gets an
    // actionable message instead.
    var oversized = (cart.items || []).some(function (item) { return Number(item.quantity) > 50; });
    if (oversized || (cart.items || []).length > 200) {
      failCheckout('Bundle checkout supports up to 50 units per item. Please lower the quantity, or order the bundle and the high-quantity items separately.');
      return;
    }

    try {
      var payload = await createDraftCheckout(cart);
      if (payload.useNativeCheckout) {
        window.location.href = '/checkout';
        return;
      }
      if (!payload.checkoutUrl) {
        throw new Error('Shopify did not return a checkout link.');
      }
      window.location.href = payload.checkoutUrl;
    } catch (error) {
      // Network / CORS failure (Supabase down, origin not in the backend
      // allowlist): show a retryable error. Never silently redirect to the
      // native checkout — it would charge full price for a visibly
      // discounted cart.
      failCheckout(error instanceof TypeError
        ? 'We could not apply your bundle discount. Please try again in a moment.'
        : (error && error.message ? error.message : 'Unable to prepare bundle checkout. Please try again.'));
    }
  }

  // ── Checkout routing ──────────────────────────────────────────────────────
  // The capture-phase listeners are ALWAYS registered; the draft-vs-native
  // decision is made per click from the live cart state. This closes the
  // race where a checkout click could land before the first async /cart.js
  // read had activated a conditional interceptor. Carts without bundle lines
  // continue to the native checkout via nativeCheckout(), which re-submits
  // the original cart form so notes/attributes are preserved.

  var lastCartHadBundle = false;
  var lastCart = null;

  /* ⚠️ A cart line key is `variantId:hash`, and Shopify REWRITES the hash half
     of EVERY line whenever the cart changes — free-gift.js adding the gift
     renumbers the bundle's lines too. So within a beat of any cart mutation
     the keys in lastCart no longer match anything in the drawer, and both the
     check below and the render itself (which finds lines by exact key) fall
     flat. Observed on live: drawer keys `…:bf8d1` against a lastCart holding
     `…:3b9db`, so the summary could not be put back until an async refetch
     brought fresh keys — the ~0.9s the summary stayed missing.

     Both helpers therefore work off the VARIANT id, the stable half. */

  function domLineKeysByVariant() {
    var byVariant = {};
    var lines = document.querySelectorAll('[data-input-item][data-key]');
    Array.prototype.forEach.call(lines, function (line) {
      var key = line.getAttribute('data-key');
      if (!key) return;
      var variant = String(key).split(':')[0];
      if (!byVariant[variant]) byVariant[variant] = [];
      byVariant[variant].push(key);
    });
    return byVariant;
  }

  function lineVariantId(item) {
    return String(item.variant_id || item.id || String(item.key || '').split(':')[0]);
  }

  /* True when the drawer is showing this bundle's raw line items with no
     summary over them — i.e. someone else's re-render dropped ours. Anchored
     on lines actually present, so a bundle the shopper genuinely removed is
     never resurrected. */
  function bundleSummaryMissing() {
    if (!lastCart) return false;
    if (document.querySelector('[data-pa-bundle-cart-summary]')) return false;
    var byVariant = domLineKeysByVariant();
    return bundleGroups(lastCart).some(function (group) {
      return group.items.some(function (item) {
        return !!byVariant[lineVariantId(item)];
      });
    });
  }

  /* Re-point a cart's line keys at the lines currently on screen, so a
     synchronous re-render can find them after Shopify has renumbered. Keys are
     claimed once each, so if a gift line ever shares a variant with a bundle
     line they cannot both resolve to the same element. */
  function withDomLineKeys(cart) {
    var byVariant = domLineKeysByVariant();
    var claimed = {};
    var clone = {};
    Object.keys(cart).forEach(function (k) { clone[k] = cart[k]; });
    clone.items = (cart.items || []).map(function (item) {
      var candidates = byVariant[lineVariantId(item)] || [];
      var domKey = null;
      for (var i = 0; i < candidates.length; i++) {
        if (!claimed[candidates[i]]) { domKey = candidates[i]; break; }
      }
      if (!domKey || domKey === item.key) return item;
      claimed[domKey] = true;
      var copy = {};
      Object.keys(item).forEach(function (k) { copy[k] = item[k]; });
      copy.key = domKey;
      return copy;
    });
    return clone;
  }

  function onCheckoutClick(event) {
    var trigger = event.target && event.target.closest
      ? event.target.closest('button[name="checkout"], input[name="checkout"], a[href="/checkout"], a[href$="/checkout"]') : null;
    if (!trigger) return;
    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
    prepareCheckout(event, trigger);
  }

  function onCheckoutSubmit(event) {
    var submitter = event.submitter;
    if (submitter && submitter.name === 'checkout') {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
      prepareCheckout(event, submitter);
    }
  }

  // Always-on: "Remove bundle" button inside the cart summary rows.
  document.addEventListener('click', function (event) {
    var removeButton = event.target && event.target.closest
      ? event.target.closest('[data-pa-remove-bundle]') : null;
    if (!removeButton) return;
    event.preventDefault();
    removeBundle(removeButton.getAttribute('data-pa-remove-bundle'), removeButton);
  }, true);

  // Checkout listeners are registered unconditionally (capture phase) so no
  // click can slip through before cart state is known.
  document.addEventListener('click', onCheckoutClick, true);
  document.addEventListener('submit', onCheckoutSubmit, true);

  // Tracks the last cart state we saw: drives the wallet-button hiding CSS
  // hook and the fallback decision when /cart.js is unreadable at click time.
  function syncBundleState(cart) {
    lastCart = cart;
    lastCartHadBundle = hasBundleLines(cart);
    if (lastCartHadBundle) {
      document.body.setAttribute('data-pa-bundle-checkout', 'draft');
    } else {
      document.body.removeAttribute('data-pa-bundle-checkout');
    }
  }

  // ── Cart refresh (summaries + bundle state) ───────────────────────────────

  async function refreshBundleCartSummaries() {
    try {
      var cart = await fetchCart();
      renderBundleCartSummaries(cart);
      syncBundleState(cart);
    } catch (_error) {}
  }

  /* Every caller here asks for SEVERAL attempts at different delays, because
     the drawer is re-rendered by code we do not control and we cannot know
     when it will settle. They used to share one timer slot and clearTimeout
     the previous one, so `scheduleBundleCartRefresh(350); scheduleBundleCartRefresh(900);`
     ran once, at 900ms -- and worse, the MutationObserver's own 180ms retry
     cancelled whatever the cart:updated handler had queued, and vice versa.
     The one mechanism that could have self-healed a wiped summary was being
     disarmed by its neighbours.

     What that cost, measured on live: free-gift.js adds the gift line in
     response to the same cart:updated we render from, so its drawer re-render
     lands AFTER our injection and wipes it (summary gone at ~0.9s, drawer
     re-rendered at ~1.1s, gift line back at ~1.5s). With every retry
     cancelling the last, nothing re-injected, and the drawer sat showing the
     bundle lines raw under an UNDISCOUNTED subtotal -- $88.96 against a
     checkout that charges $75.65 -- until the shopper navigated.

     One pending timer PER DELAY: attempts no longer cancel each other, and a
     repeated request at the same delay still collapses to one. */
  function scheduleBundleCartRefresh(delay) {
    var key = String(delay || 250);
    if (refreshTimers[key]) return;
    refreshTimers[key] = window.setTimeout(function () {
      delete refreshTimers[key];
      refreshBundleCartSummaries();
    }, delay || 250);
  }

  document.addEventListener('cart:updated', function (event) {
    if (event.detail && event.detail.cart) {
      renderBundleCartSummaries(event.detail.cart);
      syncBundleState(event.detail.cart);
    } else {
      refreshBundleCartSummaries();
    }
    scheduleBundleCartRefresh(350);
    scheduleBundleCartRefresh(900);
    // Far enough out to land after a free-gift reconcile, which measured ~1.5s
    // from this event on live. refreshBundleCartSummaries() is idempotent.
    scheduleBundleCartRefresh(2000);
  });

  document.addEventListener('quick-cart:updated', function () {
    scheduleBundleCartRefresh(250);
    scheduleBundleCartRefresh(700);
  });

  var observer = new MutationObserver(function (mutations) {
    /* Put the summary back SYNCHRONOUSLY, here, before yielding.
       A MutationObserver callback is delivered as a microtask — after the
       mutation, before the browser paints — so re-rendering inside it means
       the wiped drawer is never shown. Handing off to the timer below instead
       cost a visible flicker: the summary vanished and the subtotal jumped to
       the undiscounted $88.96 for seconds at a time while free-gift.js
       re-rendered the drawer.

       ⚠️ This runs BEFORE the suppression guard, deliberately.
       suppressCartObserver exists to stop us reacting to our OWN mutations,
       and it is held for 120ms after every render — which is precisely the
       window the theme's re-render arrives in, so a wipe that landed inside
       it went unseen and the drawer stayed wrong until a timer caught up.
       Skipping the guard is safe here because the check is a state test, not
       a mutation test: our own render always leaves the summary PRESENT, so
       it reports false immediately afterwards and cannot loop.

       lastCart may be a beat stale (the gift line may not be in it yet), but
       the bundle's own figures do not depend on it, so the price is right
       immediately and the scheduled refresh reconciles the rest. */
    if (bundleSummaryMissing()) renderBundleCartSummaries(withDomLineKeys(lastCart));

    if (suppressCartObserver) return;
    var shouldRefresh = mutations.some(function (mutation) {
      var target = mutation.target;
      return target && target.closest && (
        target.closest('.quick-cart') ||
        target.closest('.cart') ||
        (target.matches && target.matches('.quick-cart, .cart'))
      );
    });
    if (!shouldRefresh) return;
    scheduleBundleCartRefresh(180);
  });

  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refreshBundleCartSummaries);
  } else {
    refreshBundleCartSummaries();
  }
})();
